import React, { FC, useEffect, useState, useMemo } from 'react';
import { reduxForm, getFormValues } from 'redux-form';
import { compose } from 'recompose';
import { Moment } from 'moment';
import { useSelector } from "react-redux";

import { withStyles, WithStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import RestaurantView from './View';
import UserFields, { IViewType } from './ReservesDataFields';
import styles from './styles';
import { ITable } from '../../types/IRestaurantTablesStore';
import { IWorkTime } from '../../types/IScheduleStore';
import { TEditReserveInfo } from '../../containers/ReservationPageEditCont/IReserveEditPage';
import { TConfirmReserveInfo } from '../../containers/ReservationPageConfirmCont/IReserveConfirmPage';
import { TActiveTables, TUpdateReserveInfo } from '../../types/IReserveModalStore';
import { TSectionAdaptSchema } from '../../types/IRestaurantTablesSchemaStore';
import { emptyTime, reserveModal } from '../../config/constants';
import locales from '../../locales';
import { IPropsSelectOption } from '../../reducers/selectors';

import Reserve, { IAppReservesStore, TAppReserveOuterChangeType, TNewReserveFields }
  from '../../types/IAppReservesStore';

import { IAppReserveCloseOptions } from '../../actions/appReserves/IAppReservesActions';
import Nav from './Nav';
import { parsePhone } from "../../helpers/helpers";
import { IAppStore } from "../../types/IAppStore";
import TableSelectModal from './TableSelectModal';
import { useWindowSize } from '../../helpers/callHelpers';
import classNames from "classnames";

interface IProps extends WithStyles<typeof styles> {
  disabled?: boolean;
  form: string;
  outerChangeType?: TAppReserveOuterChangeType;

  title: string;
  type?: 'online' | 'new' | 'edit';
  isSchemasEnabled: boolean,
  sections: Array<IPropsSelectOption>;
  persons: number;
  section: number;
  schema: TSectionAdaptSchema | void;
  freeTables: Array<ITable> | void;
  handleCloseDialog: (options?: IAppReserveCloseOptions) => void;
  handleSelectTable: (table: ITable) => void;
  handleSubmit?: (props: any) => void;
  handleReject?: () => void;
  handleMergeTables: () => void;
  handleGetFreeTables: (field?: { value: string | number, type: string }) => void;
  handleGetCalculatedPrepayAmount?: (field?: { value: string | number, type: string }) => void;
  handleErrorMsg?: (msg: string) => void;
  handleViewChange: (sectionId: number) => void;
  activeTables: TActiveTables;
  timeList: Array<IWorkTime>;
  reserveInfo?: TEditReserveInfo | TConfirmReserveInfo | TUpdateReserveInfo;
  reserveBtnText?: string;
  rejectBtnText?: string;
  maxCapacity: number;
  isMergeTables: boolean;
  minutes?: number;
  locale?: string;

  appReserves: IAppReservesStore;
  selectedAppReserve?: Reserve;
  addAppReserve: (reserve: Reserve) => void;
  removeAppReserve: (key: string) => void;
  openAppReserve: (key: string) => void;
  closeAppReserve: (key?: string, options?: IAppReserveCloseOptions) => void;
  changeAppReserve: (name: string, value?: any) => void;
  appReserveFormValues?: TNewReserveFields;
  alreadySelectedTables: Array<number>;
  isBanquet: boolean;
}

const ReserveModal: FC<IProps> = ({
  activeTables,
  type,
  classes,
  isSchemasEnabled,
  freeTables,
  sections,
  section,
  schema,
  persons,
  handleCloseDialog,
  handleSelectTable,
  handleSubmit,
  handleGetFreeTables,
  handleGetCalculatedPrepayAmount,
  handleMergeTables,
  handleErrorMsg,
  handleViewChange,
  timeList,
  reserveInfo,
  handleReject,
  reserveBtnText,
  rejectBtnText,
  maxCapacity,
  isMergeTables,
  minutes,
  locale,

  appReserves,
  selectedAppReserve,
  // addAppReserve,
  // openAppReserve,
  closeAppReserve,
  changeAppReserve,
  // appReserveFormValues,
  alreadySelectedTables,
  disabled,
  outerChangeType,

  isBanquet,
  form,
}) => {
  const isEdit = reserveInfo && 'type' in reserveInfo && reserveInfo.type === reserveModal.EDIT;
  const isEditStyles = isEdit ? classes.btnEdit : '';

  // Получаем значения формы для проверки заполненности полей
  const formValues = useSelector((state: IAppStore) => form ? getFormValues(form)(state) : null) as any;

  // Определение размера экрана для fullscreen режима
  const isClient = typeof window !== 'undefined';
  const windowSize = useWindowSize();
  // Для ширины: используем windowSize.width с fallback на window.innerWidth
  const currentWidth = windowSize.width !== undefined ? windowSize.width : (isClient ? window.innerWidth : 0);

  const w = isClient
    ? (currentWidth < 600 ? 'xs' : currentWidth < 960 ? 'sm' : 'md')
    : 'md';
  const isPhone = w === 'xs';
  const isTablet = w === 'sm';

  // Fullscreen для телефонов (xs) или когда высота экрана меньше 600px (горизонтальная ориентация)
  // Используем windowSize.height для реактивного обновления (через useWindowSize)
  // Проверяем точно так же как для ширины: если высота < 600px, то fullscreen
  // windowSize.height используется для перерендера при изменении размера
  const currentHeight = windowSize.height !== undefined ? windowSize.height : (isClient ? window.innerHeight : 0);
  const isMobileHeight = isClient && currentHeight < 600;
  const isMobile = isPhone || isTablet || isMobileHeight;

  // Для мобильной логики отображения (скрытие RestaurantView, показ TableSelectModal)
  // используем только проверку ширины, а не высоты
  // В горизонтальном fullscreen (высота < 600px, но ширина >= 600px) показываем десктопную логику
  const isMobileLayout = isMobile; // Только для ширины < 600px
  console.log('....isMobileLayout', isMobileLayout)
  // Вычисляем ключ для отслеживания открытия нового резерва
  const reserveKey = useMemo(() => {
    const reserveId = reserveInfo && 'id' in reserveInfo ? reserveInfo.id : '';
    return `${selectedAppReserve?.key || ''}_${reserveId}`;
  }, [selectedAppReserve?.key, reserveInfo]);

  // Инициализируем viewType на основе isSchemasEnabled
  const [viewType, setViewType] = useState<IViewType>(isSchemasEnabled ? 'scheme' : 'table');
  const [tableSelectModalOpen, setTableSelectModalOpen] = useState(false);
  const [modalViewType, setModalViewType] = useState<IViewType>(isSchemasEnabled ? 'scheme' : 'table');

  useEffect(() => {
    if (viewType === 'scheme' && typeof handleViewChange === 'function') {
      // reset Section to first item in list if option "All sections" selected
      if (!section && sections && sections.length) {
        handleViewChange(sections[0].value);
      }
    }
  }, [viewType]);

  // Устанавливаем viewType в 'scheme' при открытии модального окна, если схемы включены
  // Используем reserveKey как индикатор открытия нового резерва
  useEffect(() => {
    const defaultView = isSchemasEnabled ? 'scheme' : 'table';
    setViewType(defaultView);
    setModalViewType(defaultView);
  }, [isSchemasEnabled, reserveKey]);

  const handleOpenTableSelectModal = () => {
    setModalViewType(viewType);
    setTableSelectModalOpen(true);
  };

  const handleCloseTableSelectModal = () => {
    setTableSelectModalOpen(false);
  };

  const handleSaveTableSelect = () => {
    setTableSelectModalOpen(false);
  };

  const handleModalViewChange = (newViewType: IViewType) => {
    setModalViewType(newViewType);
    if (newViewType === 'scheme' && typeof handleViewChange === 'function') {
      if (!section && sections && sections.length) {
        handleViewChange(sections[0].value);
      }
    }
  };

  const closeButton = (
    <Button
      className={classes.btnStyle}
      variant="outlined"
      onClick={() => handleCloseDialog({
        remove: true,
        openNext: true,
      })}
    >
      {locales.t('common.editReserveModal.close')}
    </Button>
  );

  let buttons = (
    <>
      <Button
        className={classNames(classes.btnStyle, isEditStyles)}
        variant={isEdit ? 'contained' : 'outlined'}
        onClick={handleReject}
      >
        {rejectBtnText}
      </Button>
      <Button
        className={classes.btnStyle}
        variant="contained"
        type="submit"
        color="primary"
      >
        {reserveBtnText}
      </Button>
    </>
  );

  if (outerChangeType === 'CANCELED') {
    buttons = (
      <>
        {closeButton}
        <div className={classes.banquetError}>
          <Typography
            variant="body2"
            color="error"
            className={classes.banquetErrorText}
          >
            {locales.t('common.editReserveModal.remoteCancel')}
          </Typography>
        </div>
      </>
    );
  } else if (disabled) {
    buttons = closeButton;
  } else if (isBanquet && type === 'edit') {
    buttons = (
      <>
        <div className={classes.banquetError}>
          <Typography
            variant="body2"
            color="error"
            className={classes.banquetErrorText}
          >
            {locales.t('common.editReserveModal.banquetMsg')}
          </Typography>
        </div>
        {closeButton}
      </>
    );
  }

  return (
    <div className={isMobile ? classes.fullscreenWrapper : undefined}>
      <form onSubmit={handleSubmit} className={classes.formWrap}>
        <Nav
          reserves={appReserves ? appReserves.items : []}
          onBack={() => closeAppReserve()}
          onClickReserve={(reserve: Reserve) => {
            if (selectedAppReserve) {
              closeAppReserve(
                selectedAppReserve.key,
                { openNextKey: reserve.key },
              );
            }
          }}
          onAddReserve={() => {
            if (selectedAppReserve) {
              closeAppReserve(
                selectedAppReserve.key,
                { openNewReserve: new Reserve('APP') },
              );
            }

            // addAppReserve(new Reserve('APP'));
          }}
        />

        <div className={classes.content}>
          <UserFields
            disabled={disabled}
            type={type}
            reserveInfo={reserveInfo}
            locale={locale}
            isMergeTables={isMergeTables}
            getFreeTables={handleGetFreeTables}
            handleMergeTables={handleMergeTables}
            getCalculatedPrepayAmount={handleGetCalculatedPrepayAmount}
            sections={sections}
            viewType={viewType}
            onViewChange={setViewType}
            handleErrorMsg={handleErrorMsg}
            minutes={minutes}
            timeList={timeList}
            maxCapacity={maxCapacity}
            onReservePropertyChange={(name, value: any) => changeAppReserve(name, value)}
            isBanquet={isBanquet}
            activeTables={activeTables}
            freeTables={freeTables}
            onOpenTableSelectModal={isMobileLayout ? handleOpenTableSelectModal : undefined}
            section={section}
            handleSelectTable={handleSelectTable}
            formValues={formValues}
            handleViewChange={handleViewChange}
            isMobile={isMobile}
            isMobileLayout={isMobileLayout}
          />

          {/* RestaurantView скрыт только на мобильных (ширина < 960px) */}
          {/* В горизонтальном fullscreen (высота < 600px, ширина >= 600px) показываем десктопную логику */}
          {!isMobileLayout && (
            <RestaurantView
              activeTables={activeTables}
              isMergeTables={isMergeTables}
              handleSelectTable={handleSelectTable}
              tables={freeTables}
              schema={schema}
              viewId={viewType}
              isSchemasEnabled={isSchemasEnabled}
              persons={isMergeTables ? 0 : persons}
              alreadySelectedTables={alreadySelectedTables}
              isMobile={isMobile}
            />
          )}
        </div>

        {/* Модалка выбора столов только для мобильных (ширина < 960px) */}
        {isMobileLayout && (
          <TableSelectModal
            open={tableSelectModalOpen}
            onClose={handleCloseTableSelectModal}
            onSave={handleSaveTableSelect}
            activeTables={activeTables}
            handleSelectTable={handleSelectTable}
            tables={freeTables}
            schema={schema}
            viewId={modalViewType}
            isSchemasEnabled={isSchemasEnabled}
            persons={isMergeTables ? 0 : persons}
            isMergeTables={isMergeTables}
            alreadySelectedTables={alreadySelectedTables}
            onViewChange={handleModalViewChange}
            sections={sections}
            section={section}
            onSectionChange={handleViewChange}
            handleMergeTables={handleMergeTables}
            onReservePropertyChange={(name, value: any) => changeAppReserve(name, value)}
            disabled={disabled}
            formValues={formValues}
          />
        )}

        <div className={classes.actions}>{buttons}</div>
      </form>
    </div>
  );
};

export type TReserveFormCurrentData = {
  firstName: string;
  persons: number;
  phone: string;
  name: string;
  date: Moment | typeof emptyTime;
  time: string;
  hours: string;
  minutes: string;
  section: number;
  isBanquet: boolean;
  prepayAmount?: string | number;
}

const validate = ({
  firstName,
  phone,
  hours,
  minutes,
  time,
  persons,
  date,
}: TReserveFormCurrentData) => {
  const errors: any = {};

  if (time === emptyTime) errors.time = true;
  if (date === emptyTime) errors.date = true;
  if (persons === 0) errors.persons = true;
  if (!firstName) errors.firstName = "Введите имя";
  if (!phone || !parsePhone(phone)) errors.phone = "Неверный номер";
  if (isNaN(Number(hours)) || isNaN(Number(minutes))) errors.minutes = "Введите длительность резерва";
  if ((Number(hours) * 60 + Number(minutes)) < 15) {
    errors.minutes = "Минимальное время резерва - 15 мин";
  }
  if ((Number(hours) * 60 + Number(minutes)) > 1435) {
    errors.minutes = "Максимальное время резерва - 23 часа 55 мин";
  }
  return errors;
};

export default compose<any, IProps>(
  withStyles(styles),
  reduxForm({
    validate,
  }),
)(ReserveModal);
